import React, { useState, useEffect } from 'react';
import radiologyConfigService from '../../services/radiologyConfigService';
import './DoctorRadiologyPreferencesModal.css';

export default function DoctorRadiologyPreferencesModal({
  isOpen,
  onClose,
  doctorId = null,
  doctorName = 'Current Radiologist',
  centreId = null,
  centreName = 'Current Centre',
  investigationId = null,
  investigationName = null,
  availableTemplates = [],
  onPreferencesSaved = null
}) {
  const [scopeMode, setScopeMode] = useState('DOCTOR'); // 'DOCTOR' | 'DOCTOR_INVESTIGATION' | 'CENTRE'
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  // Profile preferences
  const [profile, setProfile] = useState({
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    autoSaveIntervalSeconds: 60,
    showExaminationTechnique: true,
    showClinicalHistory: true,
    reportSectionOrder: ['TECHNIQUE', 'HISTORY', 'FINDINGS', 'IMPRESSION'],
    defaultTemplateId: null,
    enableSpellCheck: true,
    compactWorklistView: false
  });

  // Effective resolution trail
  const [resolutionTrail, setResolutionTrail] = useState([]);

  useEffect(() => {
    if (!isOpen) return;

    const loadConfig = async () => {
      setLoading(true);
      setErrorMsg(null);
      setStatusMsg(null);
      try {
        // 1. Load active scope config
        let res;
        const effDoctorId = doctorId || 1;
        if (scopeMode === 'DOCTOR_INVESTIGATION' && investigationId) {
          res = await radiologyConfigService.getDoctorInvestigationConfig(effDoctorId, investigationId);
        } else if (scopeMode === 'CENTRE' && centreId) {
          res = await radiologyConfigService.getCentreConfig(centreId);
        } else {
          res = await radiologyConfigService.getDoctorProfile(effDoctorId);
        }

        if (res && res.data) {
          setProfile(prev => ({
            ...prev,
            ...res.data
          }));
        }

        // 2. Resolve complete precedence trail
        const resolvedRes = await radiologyConfigService.resolveConfiguration({
          centreId: centreId || null,
          doctorId: effDoctorId,
          investigationId: investigationId || null
        });

        if (resolvedRes && resolvedRes.data && resolvedRes.data.resolutionTrail) {
          setResolutionTrail(resolvedRes.data.resolutionTrail);
        }
      } catch (err) {
        console.error('Error loading radiology configuration profile:', err);
        setErrorMsg('Failed to load profile. Using system baseline.');
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, [isOpen, scopeMode, doctorId, centreId, investigationId]);

  if (!isOpen) return null;

  const handleSave = async () => {
    setSaving(true);
    setErrorMsg(null);
    setStatusMsg(null);
    try {
      const effDoctorId = doctorId || 1;
      if (scopeMode === 'DOCTOR_INVESTIGATION' && investigationId) {
        await radiologyConfigService.saveDoctorInvestigationConfig(effDoctorId, investigationId, profile);
        setStatusMsg(`Saved Doctor + Investigation preferences for '${investigationName || 'Investigation #' + investigationId}'.`);
      } else if (scopeMode === 'CENTRE' && centreId) {
        await radiologyConfigService.saveCentreConfig(centreId, profile);
        setStatusMsg(`Saved Centre preferences for '${centreName}'.`);
      } else {
        await radiologyConfigService.saveDoctorProfile(effDoctorId, profile);
        setStatusMsg(`Saved Doctor profile preferences for Dr. ${doctorName}.`);
      }

      if (onPreferencesSaved) {
        onPreferencesSaved(profile);
      }

      setTimeout(() => {
        setStatusMsg(null);
      }, 3500);
    } catch (err) {
      console.error('Error saving radiology configuration:', err);
      setErrorMsg(err.response?.data?.message || 'Failed to save configuration.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rad-pref-backdrop" onClick={onClose}>
      <div className="rad-pref-modal" onClick={e => e.stopPropagation()}>
        <header className="rad-pref-header">
          <div className="rad-pref-title-group">
            <h3>⚙️ Radiology Configuration & Preferences</h3>
            <p>Configure doctor-personalized reporting profiles, template mappings, and layout options.</p>
          </div>
          <button className="rad-pref-close-btn" onClick={onClose} title="Close">✕</button>
        </header>

        {/* Scope Selector Tabs */}
        <div className="rad-pref-scope-tabs">
          <button
            type="button"
            className={`rad-pref-tab ${scopeMode === 'DOCTOR' ? 'active' : ''}`}
            onClick={() => setScopeMode('DOCTOR')}
          >
            🩺 Doctor Profile ({doctorName})
          </button>
          {investigationId && (
            <button
              type="button"
              className={`rad-pref-tab ${scopeMode === 'DOCTOR_INVESTIGATION' ? 'active' : ''}`}
              onClick={() => setScopeMode('DOCTOR_INVESTIGATION')}
            >
              🔬 Doctor + Investigation ({investigationName || '#' + investigationId})
            </button>
          )}
          {centreId && (
            <button
              type="button"
              className={`rad-pref-tab ${scopeMode === 'CENTRE' ? 'active' : ''}`}
              onClick={() => setScopeMode('CENTRE')}
            >
              🏥 Centre Profile ({centreName})
            </button>
          )}
        </div>

        {/* Status Alerts */}
        {statusMsg && <div className="rad-pref-alert success">✓ {statusMsg}</div>}
        {errorMsg && <div className="rad-pref-alert danger">⚠️ {errorMsg}</div>}

        <div className="rad-pref-body">
          {loading ? (
            <div className="rad-pref-loading">Loading preferences profile...</div>
          ) : (
            <div className="rad-pref-grid">
              {/* Left Column: Editor & Typography */}
              <div className="rad-pref-section">
                <h4>Editor & Typography</h4>
                
                <div className="rad-pref-field">
                  <label>Font Family</label>
                  <select
                    value={profile.fontFamily || 'Inter, sans-serif'}
                    onChange={e => setProfile({ ...profile, fontFamily: e.target.value })}
                  >
                    <option value="Inter, sans-serif">Inter (Modern Clean Sans)</option>
                    <option value="Roboto, sans-serif">Roboto (Clinical Standard)</option>
                    <option value="Arial, sans-serif">Arial (Standard Sans)</option>
                    <option value="Georgia, serif">Georgia (Traditional Serif)</option>
                    <option value="'Courier New', monospace">Courier New (Monospace)</option>
                  </select>
                </div>

                <div className="rad-pref-field">
                  <label>Font Size</label>
                  <select
                    value={profile.fontSize || '14px'}
                    onChange={e => setProfile({ ...profile, fontSize: e.target.value })}
                  >
                    <option value="12px">12px (Compact)</option>
                    <option value="13px">13px (Standard Small)</option>
                    <option value="14px">14px (Recommended Default)</option>
                    <option value="15px">15px (Medium)</option>
                    <option value="16px">16px (Large Readability)</option>
                  </select>
                </div>

                <div className="rad-pref-field">
                  <label>Auto-Save Interval</label>
                  <select
                    value={profile.autoSaveIntervalSeconds ?? 60}
                    onChange={e => setProfile({ ...profile, autoSaveIntervalSeconds: parseInt(e.target.value, 10) })}
                  >
                    <option value={0}>Disabled (Manual Save Only)</option>
                    <option value={30}>Every 30 seconds</option>
                    <option value={60}>Every 1 minute (Recommended)</option>
                    <option value={120}>Every 2 minutes</option>
                    <option value={300}>Every 5 minutes</option>
                  </select>
                </div>

                <div className="rad-pref-checkbox-field">
                  <label>
                    <input
                      type="checkbox"
                      checked={profile.enableSpellCheck ?? true}
                      onChange={e => setProfile({ ...profile, enableSpellCheck: e.target.checked })}
                    />
                    Enable Browser Spell Checking
                  </label>
                </div>
              </div>

              {/* Right Column: Template & Workflow Sections */}
              <div className="rad-pref-section">
                <h4>Template & Workflow Defaults</h4>

                <div className="rad-pref-field">
                  <label>
                    Default Template {scopeMode === 'DOCTOR_INVESTIGATION' ? `for ${investigationName || 'this test'}` : ''}
                  </label>
                  <select
                    value={profile.defaultTemplateId || ''}
                    onChange={e => setProfile({ ...profile, defaultTemplateId: e.target.value ? parseInt(e.target.value, 10) : null })}
                  >
                    <option value="">-- Use Investigation Default --</option>
                    {availableTemplates.map(tmpl => (
                      <option key={tmpl.templateId} value={tmpl.templateId}>
                        {tmpl.title || tmpl.templateTitle} (v{tmpl.version || 1} - {tmpl.scope || 'SHARED'})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="rad-pref-checkbox-field">
                  <label>
                    <input
                      type="checkbox"
                      checked={profile.showExaminationTechnique ?? true}
                      onChange={e => setProfile({ ...profile, showExaminationTechnique: e.target.checked })}
                    />
                    Display 'Examination Technique' section
                  </label>
                </div>

                <div className="rad-pref-checkbox-field">
                  <label>
                    <input
                      type="checkbox"
                      checked={profile.showClinicalHistory ?? true}
                      onChange={e => setProfile({ ...profile, showClinicalHistory: e.target.checked })}
                    />
                    Display 'Clinical History' section
                  </label>
                </div>

                <div className="rad-pref-checkbox-field">
                  <label>
                    <input
                      type="checkbox"
                      checked={profile.compactWorklistView ?? false}
                      onChange={e => setProfile({ ...profile, compactWorklistView: e.target.checked })}
                    />
                    Compact Worklist View mode
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Configuration Hierarchy Precedence Visualizer */}
          <div className="rad-pref-hierarchy-box">
            <h5>Configuration Precedence Hierarchy (Highest specificity wins):</h5>
            <div className="rad-hierarchy-steps">
              <span className="step-pill">1. SYSTEM DEFAULT</span>
              <span className="step-arrow">→</span>
              <span className="step-pill">2. CENTRE</span>
              <span className="step-arrow">→</span>
              <span className="step-pill">3. DEPARTMENT</span>
              <span className="step-arrow">→</span>
              <span className={`step-pill ${scopeMode === 'DOCTOR' ? 'active-scope' : ''}`}>4. DOCTOR</span>
              <span className="step-arrow">→</span>
              <span className={`step-pill ${scopeMode === 'DOCTOR_INVESTIGATION' ? 'active-scope' : ''}`}>5. DOCTOR + INVESTIGATION</span>
              <span className="step-arrow">→</span>
              <span className="step-pill">6. TRANSACTION OVERRIDE</span>
            </div>

            {resolutionTrail.length > 0 && (
              <div className="rad-trail-summary">
                <strong>Active Resolution Audit:</strong> {resolutionTrail.map(t => `${t.level}: ${t.description}`).join(' ➔ ')}
              </div>
            )}
          </div>
        </div>

        <footer className="rad-pref-footer">
          <button type="button" className="btn-cancel" onClick={onClose} disabled={saving}>
            Close
          </button>
          <button type="button" className="btn-save" onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </footer>
      </div>
    </div>
  );
}
